import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    expiredMembers: 0,
    overduePayments: 0,
    monthlyCollections: 0,
    totalRevenue: 0,
    recentMembers: [],
    upcomingRenewals: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Single optimized API call - stats endpoint returns everything we need
      const statsRes = await axios.get('/api/dashboard/stats', { headers });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate days until renewal
  const getDaysUntil = (dateString) => {
    if (!dateString) return 999;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(dateString);
    dueDate.setHours(0, 0, 0, 0);
    return Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
  };

  if (loading) return <div className="loading">Loading dashboard...</div>;

  return (
    <div className="dashboard">
      <h1>📊 Dashboard</h1>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>👥 Total Members</h3>
          <div className="value">{stats.totalMembers}</div>
        </div>
        <div className="stat-card success">
          <h3>✅ Active Members</h3>
          <div className="value">{stats.activeMembers}</div>
        </div>
        <div className="stat-card danger">
          <h3>⚠️ Expiring Soon</h3>
          <div className="value">{stats.upcomingRenewals?.length || 0}</div>
        </div>
        {/* Revenue Cards - Admin Only */}
        {isAdmin && (
          <>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <h3 style={{ color: 'rgba(255,255,255,0.9)' }}>💰 Total Revenue</h3>
              <div className="value" style={{ color: 'white' }}>₹{stats.totalRevenue?.toLocaleString() || 0}</div>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #5a67d8 0%, #667eea 100%)', color: 'white' }}>
              <h3 style={{ color: 'rgba(255,255,255,0.9)' }}>📅 Monthly Collections</h3>
              <div className="value" style={{ color: 'white' }}>₹{stats.monthlyCollections?.toLocaleString() || 0}</div>
              <small style={{ opacity: 0.9 }}>This month</small>
            </div>
          </>
        )}
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginTop: '20px' }}>
        
        {/* Recent Members List */}
        <div className="table-container">
          <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>🆕 Recent Members</h2>
          </div>
          {stats.recentMembers?.length > 0 ? (
            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Expiry</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentMembers.map(member => (
                    <tr key={member.id}>
                      <td><strong>{member.name}</strong></td>
                      <td>{member.phone}</td>
                      <td>{formatDate(member.expiry)}</td>
                      <td>
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '15px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          background: member.status === 'Active' ? '#4CAF50' : member.status === 'Overdue' ? '#FF9800' : '#f44336',
                          color: 'white'
                        }}>
                          {member.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <h3>No members yet</h3>
            </div>
          )}
        </div>

        {/* Expiring Soon */}
        <div className="table-container" style={{ borderLeft: '4px solid #FF9800' }}>
          <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>⏰ Expiring Soon ({stats.upcomingRenewals?.length || 0})</h2>
          </div>
          {stats.upcomingRenewals?.length > 0 ? (
            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Expires</th>
                    <th>Days Left</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.upcomingRenewals.map(member => {
                    const daysLeft = getDaysUntil(member.renewalDue);
                    return (
                      <tr key={member.id} style={{ background: daysLeft <= 2 ? '#fff3e0' : 'transparent' }}>
                        <td><strong>{member.name}</strong></td>
                        <td>{member.phone}</td>
                        <td>{formatDate(member.renewalDue)}</td>
                        <td>
                          <span style={{
                            padding: '3px 10px',
                            borderRadius: '15px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            background: daysLeft <= 2 ? '#f44336' : daysLeft <= 5 ? '#FF9800' : '#4CAF50',
                            color: 'white'
                          }}>
                            {daysLeft === 0 ? 'Today!' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <a 
                              href={`https://wa.me/91${member.phone?.replace(/\D/g, '')}?text=Hi ${member.name}, your gym membership is expiring ${daysLeft === 0 ? 'today' : `in ${daysLeft} days`}. Please renew to continue your fitness journey!`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                              title="Send WhatsApp"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="#25D366">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state" style={{ padding: '30px', textAlign: 'center' }}>
              <h3>🎉 No expiring members</h3>
              <p>All members have time remaining on their plans</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
